import React, { FC, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import { Link, useNavigate } from 'react-router-dom';
import { PhotoResponseDto, SupportedLanguageIso, UserLoginResDto } from '@docbay/schemas';

import { Divider, Input, PrimaryButton } from 'application/components';
import { PathNames } from 'application/routes/constants';
import { getLanguageFromLocalStorage } from 'application/helpers/language';
import { UsersActions } from 'application/store/reducers/Users/actions';
import { useAppDispatch, useAppSelector } from 'application/hooks/redux';
import { isResponseFulfilled } from 'application/helpers/responseHelper';
import { requestSelector } from 'application/store/reducers/request/selectors';
import {
  getToken,
  setProfilePermissions,
} from 'application/helpers/sessionStorage/auth';

import { Loader } from 'common/loader';

import { useLoginSchema } from '../helpers/useLoginSchema';
import LayoutComponent from '../LayoutComponent';
import { login } from '../authService';

import { LoginFormData } from './models';
import {
  Container,
  TextSection,
  FormStyled,
  RowStyled,
  FooterStyled,
  StyledError,
} from './style';

export interface LoginResponseHrd extends UserLoginResDto {
  user?: {
    id: string;
    email: string;
    phone: string;
    firstName: string;
    lastName: string;
    photo: PhotoResponseDto;
    companyId: string;
  };
}

/**
 * Functional component for the login page.
 * @returns JSX representing the login page.
 */
const Login: FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const loginSchema = useLoginSchema();
  const { loading } = useAppSelector(requestSelector).usersLogin;

  const [showError, setShowError] = useState(false);

  const {
    register,
    handleSubmit,
    setError,
    watch,
    clearErrors,
    formState: { errors },
  } = useForm<LoginFormData>({
    resolver: yupResolver(loginSchema),
    mode: 'onChange',
  });

  // This effect checks if there is a token stored in the session storage.
  // If there is, it means that the user is already logged in, so we navigate them to the home page.
  useEffect(() => {
    const token = getToken();
    if (token) return navigate(PathNames.home);
  }, []);

  // This effect clears any form errors when the email or password fields are changed.
  // It also sets the showError state to false.
  useEffect(() => {
    if (showError) {
      clearErrors();
      setShowError(false);
    }
  }, [watch('email'), watch('password')]);

  const onSubmit = async (data: LoginFormData) => {
    const language = getLanguageFromLocalStorage();

    const response = await dispatch(
      UsersActions.usersLogin({
        email: data.email,
        password: data.password,
        languageIso: language as SupportedLanguageIso,
      }),
    );

    // Safely set companyId in localStorage only if it exists on the response payload
    const companyId = (response.payload as LoginResponseHrd)?.user?.companyId ;
    if (companyId) {
      localStorage.setItem("companyId", companyId);
    }

    isResponseFulfilled(
      response,
      async () => {
        const response = await dispatch(UsersActions.fetchUserProfile());
        isResponseFulfilled(response, () => {
          setProfilePermissions(response.payload.permissions);
          login();
        });
      },
      () => {
        setShowError(true);
        setError('email', {
          type: 'required',
          message: t('errors.required'),
        });
        setError('password', {
          type: 'required',
          message: t('errors.required'),
        });
      },
    );
  };

  return (
    <LayoutComponent>
      {loading && <Loader />}
      <Container className="mr-120">
        <TextSection>
          <h1>{t('log_in.title')}</h1>
          <h2>{t('log_in.sub_title')}</h2>
        </TextSection>
        <FormStyled onSubmit={handleSubmit(onSubmit)}>
          <Input
            id="email"
            label={t('global.email') || ''}
            type="email"
            placeholder={t('global.email_placeholder') || ''}
            errorMessage={errors.email?.message}
            register={register}
            hideErrorMessage={showError}
          />
          <Input
            id="password"
            label={t('global.password')!}
            type="password"
            placeholder={t('global.password_placeholder')!}
            errorMessage={errors.password?.message}
            register={register}
            hideErrorMessage={showError}
          />
          <RowStyled>
            <Link to={PathNames.forgotPassword}>
              {t('log_in.forgot_password')}
            </Link>
          </RowStyled>
          <PrimaryButton type="submit">{t('global.login')}</PrimaryButton>
          {showError && (
            <StyledError>{t('errors.incorrect_credentials')}</StyledError>
          )}
        </FormStyled>
        <FooterStyled>
          <div>
            <Divider />
            <p>{t('global.or')}</p>
            <Divider />
          </div>
          <div>
            <p>{t('log_in.dont_have_an_account')}</p>
            <Link to={PathNames.companyRegistration}>
              {t('global.register')}
            </Link>
          </div>
        </FooterStyled>
      </Container>
    </LayoutComponent>
  );
};

export default Login;
